import { useCallback } from "react";
import { isPlainObject } from "remeda";
import { create } from "zustand";

/** Ordered UI state path from broader scope to a more specific leaf. */
export type UIStateKey = readonly string[];

/** Nested UI state object stored in memory and persisted to localStorage. */
export type UIStateTree = Record<string, unknown>;

/** Local storage key for persisted UI state. */
export const UI_STATE_STORAGE_KEY = "idcat_ui_state";

/** Appends a component-owned scope segment to an optional parent UI state scope. */
export const appendUiStateScope = (
  outerScope: UIStateKey | undefined,
  innerScope: string | UIStateKey,
): UIStateKey | undefined => {
  if (!outerScope) {
    return undefined;
  }

  return typeof innerScope === "string"
    ? [...outerScope, innerScope]
    : [...outerScope, ...innerScope];
};

/** Retrieve a value from nested UI state tree. */
const getStoredUIStateValue = (
  uiState: UIStateTree,
  key: UIStateKey,
): unknown => {
  let currentValue: unknown = uiState;

  for (const segment of key) {
    if (!isPlainObject(currentValue) || !(segment in currentValue)) {
      return undefined;
    }

    currentValue = currentValue[segment];
  }

  return currentValue;
};

const getValidatedUIStateValue = <T>(
  uiState: UIStateTree,
  key: UIStateKey,
  fallbackValue: T,
  validator: (value: unknown) => value is T,
): T => {
  const storedValue = getStoredUIStateValue(uiState, key);

  return validator(storedValue) ? storedValue : fallbackValue;
};

/** Sets a value in the nested UI state tree. Returns a new tree with the updated value. */
const setStoredUIStateValue = (
  uiState: UIStateTree,
  key: UIStateKey,
  value: unknown,
): UIStateTree => {
  if (key.length === 0) {
    return uiState;
  }

  const [currentSegment, ...restKey] = key;

  if (restKey.length === 0) {
    return {
      ...uiState,
      [currentSegment]: value,
    };
  }

  // Missing branches are created on demand so callers only provide the leaf path.
  const nestedValue = uiState[currentSegment];
  const nestedUiState = isPlainObject(nestedValue) ? nestedValue : {};

  return {
    ...uiState,
    [currentSegment]: setStoredUIStateValue(nestedUiState, restKey, value),
  };
};

const clearStoredUIStateValue = (
  uiState: UIStateTree,
  key: UIStateKey,
): UIStateTree => {
  if (key.length === 0) {
    return uiState;
  }

  const [currentSegment, ...restKey] = key;

  if (!(currentSegment in uiState)) {
    return uiState;
  }

  if (restKey.length === 0) {
    const nextUiState = { ...uiState };
    delete nextUiState[currentSegment];
    return nextUiState;
  }

  const nestedValue = uiState[currentSegment];

  if (!isPlainObject(nestedValue)) {
    return uiState;
  }

  const clearedNestedUiState = clearStoredUIStateValue(nestedValue, restKey);

  // Empty parent branches are pruned so persisted state does not accumulate dead structure.
  if (Object.keys(clearedNestedUiState).length === 0) {
    const nextUiState = { ...uiState };
    delete nextUiState[currentSegment];
    return nextUiState;
  }

  return {
    ...uiState,
    [currentSegment]: clearedNestedUiState,
  };
};

const getUIStateStorage = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return undefined;
  }

  return window.localStorage;
};

const readPersistedUIState = (): UIStateTree => {
  const storage = getUIStateStorage();

  if (!storage) {
    return {};
  }

  try {
    const storedValue = storage.getItem(UI_STATE_STORAGE_KEY);

    if (!storedValue) {
      return {};
    }

    const parsedValue: unknown = JSON.parse(storedValue);

    return isPlainObject(parsedValue) ? parsedValue : {};
  } catch {
    return {};
  }
};

const writePersistedUIState = (uiState: UIStateTree) => {
  const storage = getUIStateStorage();

  if (!storage) {
    return;
  }

  if (Object.keys(uiState).length === 0) {
    storage.removeItem(UI_STATE_STORAGE_KEY);
    return;
  }

  try {
    storage.setItem(UI_STATE_STORAGE_KEY, JSON.stringify(uiState));
  } catch {
    // Persistence failed (e.g. QuotaExceededError in private browsing).
    // In-memory state remains correct; next page load will not reflect changes.
  }
};

/** Subscribes to cross-tab localStorage updates and rehydrates the UI state store. */
export const subscribeToUIStateStorage = () => {
  if (typeof window === "undefined" || !window.addEventListener) {
    return () => undefined;
  }

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key !== null && event.key !== UI_STATE_STORAGE_KEY) {
      return;
    }

    useUIState.getState().hydrateUiState();
  };

  window.addEventListener("storage", handleStorageChange);

  return () => {
    window.removeEventListener("storage", handleStorageChange);
  };
};

type TypedStorageGetter<T> = (key: UIStateKey, fallbackValue: T) => T;
type TypedStorageSetter<T> = (key: UIStateKey, value: T) => void;
type UIStateSetterInput<T> = T | ((currentValue: T) => T);
type UIStateHookParams<T> = {
  stateKey: UIStateKey | undefined;
  fallbackValue: T;
};

interface UIStateStore {
  uiState: UIStateTree;
  hydrateUiState: () => void;
  getUiState: TypedStorageGetter<unknown>;
  getBooleanUiState: TypedStorageGetter<boolean>;
  getNumberUiState: TypedStorageGetter<number>;
  getStringUiState: TypedStorageGetter<string>;
  getStringArrayUiState: TypedStorageGetter<string[]>;
  setUiState: TypedStorageSetter<unknown>;
  clearUiState: (key: UIStateKey) => void;
}

/** Abstract base hook for creating typed UI state get-set hooks. */
const useTypedUIState = <T>(
  { stateKey, fallbackValue }: UIStateHookParams<T>,
  selectGetter: (state: UIStateStore) => TypedStorageGetter<T>,
) => {
  const getTypedUiState = useUIState(selectGetter);
  const value = useUIState((state) =>
    stateKey ? selectGetter(state)(stateKey, fallbackValue) : fallbackValue,
  );
  const setUiState = useUIState((state) => state.setUiState);

  const setValue = useCallback(
    (nextValue: UIStateSetterInput<T>) => {
      if (!stateKey) {
        return;
      }

      const resolvedValue =
        typeof nextValue === "function"
          ? (nextValue as (currentValue: T) => T)(
              getTypedUiState(stateKey, fallbackValue),
            )
          : nextValue;

      setUiState(stateKey, resolvedValue);
    },
    [stateKey, fallbackValue, getTypedUiState, setUiState],
  );

  return [value, setValue] as const;
};

/** State-like hook for boolean UI state persisted by key. */
export const useBooleanUIState = (params: UIStateHookParams<boolean>) =>
  useTypedUIState<boolean>(params, (state) => state.getBooleanUiState);

/** State-like hook for number UI state persisted by key. */
export const useNumberUIState = (params: UIStateHookParams<number>) =>
  useTypedUIState<number>(params, (state) => state.getNumberUiState);

/** State-like hook for string UI state persisted by key. */
export const useStringUIState = <T extends string = string>(
  params: UIStateHookParams<T>,
) =>
  useTypedUIState<T>(
    params,
    (state) => state.getStringUiState as unknown as TypedStorageGetter<T>,
  );

/** State-like hook for string-array UI state persisted by key. */
export const useStringArrayUIState = (params: UIStateHookParams<string[]>) =>
  useTypedUIState<string[]>(params, (state) => state.getStringArrayUiState);

/** Shared UI state store with localStorage-backed hydration and updates. */
export const useUIState = create<UIStateStore>((set, get) => ({
  uiState: readPersistedUIState(),
  hydrateUiState: () => {
    set({ uiState: readPersistedUIState() });
  },
  getUiState: (key, fallbackValue) => {
    const storedValue = getStoredUIStateValue(get().uiState, key);

    return storedValue === undefined
      ? fallbackValue
      : (storedValue as typeof fallbackValue);
  },
  getBooleanUiState: (key, fallbackValue) =>
    getValidatedUIStateValue(
      get().uiState,
      key,
      fallbackValue,
      (value): value is boolean => typeof value === "boolean",
    ),
  getNumberUiState: (key, fallbackValue) =>
    getValidatedUIStateValue(
      get().uiState,
      key,
      fallbackValue,
      (value): value is number => typeof value === "number",
    ),
  getStringUiState: (key, fallbackValue) =>
    getValidatedUIStateValue(
      get().uiState,
      key,
      fallbackValue,
      (value): value is string => typeof value === "string",
    ),
  getStringArrayUiState: (key, fallbackValue) =>
    getValidatedUIStateValue(
      get().uiState,
      key,
      fallbackValue,
      (value): value is string[] =>
        Array.isArray(value) && value.every((item) => typeof item === "string"),
    ),
  setUiState: (key, value) => {
    const nextUiState = setStoredUIStateValue(get().uiState, key, value);

    writePersistedUIState(nextUiState);

    set({ uiState: nextUiState });
  },
  clearUiState: (key) => {
    const nextUiState = clearStoredUIStateValue(get().uiState, key);

    writePersistedUIState(nextUiState);

    set({ uiState: nextUiState });
  },
}));
